import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import type { BeautyParameters } from "../services/beautyPresets";

export interface BeautyPresetPreviewProps {
  parameters: BeautyParameters;
  label?: string;
}

type PreviewStage =
  | "initializing"
  | "engine-loading"
  | "engine-ready"
  | "source-loading"
  | "source-ready"
  | "analysis-loading"
  | "analysis-ready"
  | "rendering"
  | "ready"
  | "error"
  | "adapter-ready";

type PreviewMessage = {
  type?: string;
  stage?: PreviewStage;
  error?: string;
  generation?: number;
  renderSerial?: number;
  sourceSerial?: number;
  faceStatus?: string;
  beautyMaskStatus?: string;
};

type PreviewSource =
  | { kind: "url"; url: string }
  | { kind: "file"; file: File };

const DEFAULT_DEMO_IMAGE = `${import.meta.env.BASE_URL}assets/img/gpupixel-demo-default.png`;
const DEFAULT_SOURCE: PreviewSource = { kind: "url", url: DEFAULT_DEMO_IMAGE };
const EMBEDDED_RUNTIME_PAGE = `${import.meta.env.BASE_URL}gpupixel-runtime/preview-adapter.html`;

function stageLabel(stage: PreviewStage, error: string): string {
  if (error) return `预览失败：${error}`;
  switch (stage) {
    case "adapter-ready": return "预览页面已加载，等待 GPUPixel 引擎";
    case "engine-loading": return "正在初始化本地 GPUPixel WebGL2 引擎…";
    case "engine-ready": return "GPUPixel 已就绪，正在读取当前图片…";
    case "source-loading": return "正在读取当前图片（仅浏览器内处理，不会上传）…";
    case "source-ready": return "图片已读取，正在分析人脸与主人物范围…";
    case "analysis-loading": return "正在分析人脸与皮肤遮罩…";
    case "analysis-ready": return "人脸分析完成，正在生成当前预设…";
    case "rendering": return "正在渲染当前预设…";
    case "ready": return "已使用当前预设生成效果。";
    default: return "准备本地 GPUPixel 预览…";
  }
}

export default function BeautyPresetPreview({ parameters, label = "本地 GPUPixel 预览" }: BeautyPresetPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const frameReadyRef = useRef(false);
  const commandGenerationRef = useRef(0);
  const latestRenderSerialRef = useRef(-1);
  const sourceRef = useRef<PreviewSource>(DEFAULT_SOURCE);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [frameReady, setFrameReady] = useState(false);
  const [frameLoadSerial, setFrameLoadSerial] = useState(0);
  const [stage, setStage] = useState<PreviewStage>("initializing");
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);

  const parametersKey = useMemo(() => JSON.stringify(parameters), [parameters]);
  const parametersRef = useRef(parameters);
  parametersRef.current = parameters;

  const post = useCallback((message: Record<string, unknown>) => {
    const frame = iframeRef.current;
    if (!frameReadyRef.current || !frame?.contentWindow) return false;
    frame.contentWindow.postMessage(message, window.location.origin);
    return true;
  }, []);

  const sendSource = useCallback((source: PreviewSource) => {
    const generation = ++commandGenerationRef.current;
    const sent = post({
      type: "gpupixel-preview-command",
      action: "set-source",
      generation,
      source,
    });
    if (sent && mountedRef.current) {
      setError("");
      setRetrying(false);
      setStage("source-loading");
    }
    return sent;
  }, [post]);

  const sendParameters = useCallback(() => {
    const generation = ++commandGenerationRef.current;
    const sent = post({
      type: "set-parameters",
      generation,
      parameters: parametersRef.current,
    });
    if (sent && mountedRef.current) {
      setError("");
      setRetrying(false);
      setStage("rendering");
    }
    return sent;
  }, [post]);

  const handleFrameLoad = useCallback(() => {
    if (!mountedRef.current) return;
    frameReadyRef.current = true;
    latestRenderSerialRef.current = -1;
    setFrameReady(true);
    setFrameLoadSerial((serial) => serial + 1);
    setError("");
    setRetrying(false);
    setStage("initializing");
    sendSource(sourceRef.current);
  }, [sendSource]);

  const retry = useCallback(() => {
    if (!frameReadyRef.current) return;
    setRetrying(true);
    setError("");
    setStage("analysis-loading");
    post({
      type: "retry",
      generation: ++commandGenerationRef.current,
    });
  }, [post]);

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    const source: PreviewSource = { kind: "file", file };
    sourceRef.current = source;
    setSelectedFile(file);
    setError("");
    setRetrying(false);
    if (frameReadyRef.current) {
      sendSource(source);
    } else {
      setStage("initializing");
    }
  }, [sendSource]);

  const restoreDefault = useCallback(() => {
    sourceRef.current = DEFAULT_SOURCE;
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setError("");
    setRetrying(false);
    if (frameReadyRef.current) {
      sendSource(DEFAULT_SOURCE);
    } else {
      setStage("initializing");
    }
  }, [sendSource]);

  useEffect(() => {
    if (!frameReady) return;
    sendParameters();
  }, [frameLoadSerial, frameReady, parametersKey, sendParameters]);

  useEffect(() => {
    mountedRef.current = true;
    const onMessage = (event: MessageEvent<PreviewMessage>) => {
      const frame = iframeRef.current;
      if (!frame || event.source !== frame.contentWindow || event.origin !== window.location.origin) return;
      const message = event.data;
      if (!message || message.type !== "gpupixel-preview-state") return;
      if (!Number.isFinite(message.generation) || message.generation !== commandGenerationRef.current) return;
      if (Number.isFinite(message.renderSerial)) {
        if ((message.renderSerial as number) < latestRenderSerialRef.current) return;
        latestRenderSerialRef.current = message.renderSerial as number;
      }
      if (!mountedRef.current) return;
      if (message.stage === "error") {
        setRetrying(false);
        setError(message.error || "GPUPixel 预览失败。未使用任何替代效果。");
        setStage("error");
        return;
      }
      if (message.stage) setStage(message.stage);
      if (message.stage === "ready" || message.stage === "analysis-ready") setRetrying(false);
    };
    window.addEventListener("message", onMessage);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("message", onMessage);
      post({ type: "dispose" });
      frameReadyRef.current = false;
    };
  }, [post]);

  const currentImageName = selectedFile?.name || "GPUPixel 官方示例图";

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50 p-3" aria-label={label}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-800">{label}</h3>
          <p className="mt-0.5 text-[11px] leading-4 text-slate-500" aria-live="polite">{stageLabel(stage, error)}</p>
        </div>
        <button
          type="button"
          onClick={retry}
          disabled={!frameReady || retrying}
          className="shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-cyan-400 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {retrying ? "重试中…" : "重试"}
        </button>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label
          htmlFor="beauty-preview-image-input"
          className="cursor-pointer rounded-md border border-cyan-300 bg-white px-2.5 py-1.5 text-xs font-medium text-cyan-700 hover:border-cyan-500 hover:text-cyan-800 focus-within:ring-2 focus-within:ring-cyan-400"
        >
          选择图片/上传图片
          <input
            ref={fileInputRef}
            id="beauty-preview-image-input"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="sr-only"
            aria-describedby="beauty-preview-image-help"
          />
        </label>
        <button
          type="button"
          onClick={restoreDefault}
          disabled={!selectedFile}
          className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-cyan-400 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          恢复官方示例图
        </button>
        <span className="min-w-0 truncate text-xs text-slate-600" title={currentImageName} aria-live="polite">
          当前图片：{currentImageName}
        </span>
      </div>
      <p id="beauty-preview-image-help" className="mb-2 text-[11px] leading-4 text-slate-500">
        图片只在当前浏览器中处理，选择的本地文件不会上传；可随时恢复 GPUPixel 官方示例图。
      </p>
      <div className="overflow-hidden rounded-lg border border-slate-300 bg-slate-950" style={{ minHeight: 360 }}>
        <iframe
          ref={iframeRef}
          title="GPUPixel 本地照片预览"
          src={EMBEDDED_RUNTIME_PAGE}
          onLoad={handleFrameLoad}
          className="block h-[420px] w-full border-0"
          loading="eager"
        />
      </div>
      <p className="mt-2 text-[11px] leading-4 text-slate-500">
        预览使用 GPUPixel/WebGL2 与 Android 相同的十项参数单位和主脸规则，但桌面 GPU、浏览器驱动及模型推理可能产生非逐像素一致结果。
      </p>
      {error && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs leading-5 text-red-700" role="alert">
          {error}
          <button type="button" onClick={retry} className="ml-2 font-semibold underline" disabled={!frameReady || retrying}>再次重试</button>
        </div>
      )}
    </section>
  );
}
